import { useAppSelector } from 'app/hooks';
import { AxiosError } from 'axios';
import AlertModal from 'components/common/AlertModal';
import FeatureTitleBar from 'components/common/FeatureTitleBar';
import useGlobalModal from 'components/common/GlobalModal/useGlobalModal';
import { selectIsEditMode } from 'features/common/editModeSlice';
import { Genre } from 'features/Genre/models/Genre';
import { Course } from 'features/SaleType/models/Course';
import { MenuBook } from 'features/SaleType/models/MenuBook';
import { SubGenre } from 'features/SubGenre/models/SubGenre';
import { useEffect, useState } from 'react';
import { Button, Col, Form, Modal, Navbar, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { TfiAngleRight } from 'react-icons/tfi';
import { VscFoldDown } from 'react-icons/vsc';
import service, { CopyRequest } from './services';
export interface DataProps {
  menuBookId: number | string;
  menuBookName: string | null;
  courseId: number | string;
  courseName: string | null;
  genreId: number | string;
  genreName: string | null;
  subGenreId: number | string;
  subGenreName: string | null;
  menuId: number | string;
  menuName: string | null;
  show?: boolean;
  onClose?: () => void;
  onSaveSuccess: (status: boolean) => void;
}

function CopyModal({
  onSaveSuccess,
  menuBookId,
  menuBookName,
  courseId,
  courseName,
  genreId,
  genreName,
  subGenreId,
  subGenreName,
  menuId,
  menuName,
  onClose,
  show,
}: DataProps) {
  function handleClose() {
    onClose && onClose();
  }
  const { showGlobalModal } = useGlobalModal();
  const [menuBooks, setMenuBooks] = useState<MenuBook[]>();
  const [courses, setCourses] = useState<Course[]>();
  const [genres, setGenres] = useState<Genre[]>([]);
  const [subGenres, setSubGenres] = useState<SubGenre[]>([]);
  const [requestMenuBookId, setMenuBookId] = useState<string>('');
  const [requestCourseId, setCourseId] = useState<string>('');
  const [requestGenreId, setGenre] = useState<string>('');
  const [requestSubGenreId, setSubGenre] = useState<string>('');
  const { t } = useTranslation();

  // selectors
  const isEditMode = useAppSelector(selectIsEditMode);

  async function callApiGetMenuBooks() {
    await service.getMenuBooks().then((result) => {
      setMenuBooks(result.data);
    });
  }

  async function callApiGetCourses() {
    await service.getCourses().then((result) => {
      setCourses(result.data);
    });
  }

  async function callApiGetGenres(menuBookId: string | number, courseId: string | number) {
    await service.getGenres(menuBookId, courseId).then((result) => {
      setGenres(result.data);
    });
  }

  async function callApiGetSubGenres(genreId: string | number, menuBookId: string | number, courseId: string | number) {
    await service.getSubGenres(genreId, menuBookId, courseId).then((result) => {
      setSubGenres(result.data);
    });
  }

  useEffect(() => {
    if (menuBookId) {
      callApiGetMenuBooks();
    }
    if (courseId) {
      callApiGetCourses();
    }
  }, [menuBookId, courseId]);

  function handleChangeMenuBookOrCourse(event: React.ChangeEvent<HTMLSelectElement>) {
    const value = event.target.value;
    if (menuBookId) {
      setMenuBookId(value);
      callApiGetGenres(value, requestCourseId);
    }
    if (courseId) {
      setCourseId(value);
      callApiGetGenres(requestMenuBookId, value);
    }
    setGenres([]);
    setSubGenres([]);
    setGenre('');
    setSubGenre('');
  }

  function handleChangeGenre(event: React.ChangeEvent<HTMLSelectElement>) {
    const value = event.target.value;
    callApiGetSubGenres(value, requestMenuBookId, requestCourseId);
    setGenre(value);
    setSubGenres([]);
    setSubGenre('');
  }

  function handleChangeSubGenre(event: React.ChangeEvent<HTMLSelectElement>) {
    const value = event.target.value;
    setSubGenre(value);
  }

  async function handleSubmitCopy() {
    if (menuBookId && genreId && requestMenuBookId === '') {
      showGlobalModal &&
        showGlobalModal(() => <AlertModal title={'失敗'} message={'メニューブックを選択してください'}></AlertModal>);
    } else if (courseId && genreId && requestCourseId === '') {
      showGlobalModal &&
        showGlobalModal(() => <AlertModal title={'失敗'} message={'コースを選択してください'}></AlertModal>);
    } else if ((menuBookId || courseId) && genreId && subGenreId && requestGenreId === '') {
      showGlobalModal &&
        showGlobalModal(() => <AlertModal title={'失敗'} message={t('ジャンルを選択してください')}></AlertModal>);
    } else if ((menuBookId || courseId) && genreId && subGenreId && menuId && requestSubGenreId === '') {
      showGlobalModal &&
        showGlobalModal(() => <AlertModal title={'失敗'} message={t('サブジャンルを選択してください')}></AlertModal>);
    } else {
      const formDataSubmit: CopyRequest = {
        from_menu_book_id: menuBookId,
        from_course_id: courseId,
        from_genre_id: genreId,
        from_sub_genre_id: subGenreId,
        from_menu_id: menuId,
        to_menu_book_id: requestMenuBookId,
        to_course_id: requestCourseId,
        to_genre_id: requestGenreId,
        to_sub_genre_id: requestSubGenreId,
      };
      await service
        .check(formDataSubmit)
        .then(async (res) => {
          await service.copy(formDataSubmit).then(() => {
            return onSaveSuccess && onSaveSuccess(true);
          });
        })
        .catch(async (error: AxiosError) => {
          const data = error.response?.data as any;
          if (window.confirm(data.message)) {
            await service.copy(formDataSubmit).then(() => {
              return onSaveSuccess && onSaveSuccess(true);
            });
          }
        });
    }
  }

  return (
    <Modal show={show} backdrop="static" onHide={handleClose} centered>
      <Modal.Header closeButton className={isEditMode ? 'reedit-mode-wrapper' : ''}>
        <FeatureTitleBar title={`${t('DuplicationDisplayModal.title')} | ${genreName} | ${subGenreName}`} />
      </Modal.Header>
      <Modal.Body className={isEditMode ? 'reedit-mode-wrapper' : ''}>
        <Form.Group as={Row} className="mb-2">
          <Col sm={4} md={4} lg={4} xl={4} className="mb-2" style={{ textDecoration: 'underline' }}>
            {t('DuplicationDisplayModal.from')}
          </Col>
          <Col sm={8} md={8} lg={8} xl={8}>
            <Navbar>
              <div>
                {menuBookId ? menuBookName : courseName} <TfiAngleRight />
                {genreId ? genreName : ''} {genreId && subGenreId ? <TfiAngleRight /> : ''}
                {genreId && subGenreId ? subGenreName : ''} {genreId && subGenreId && menuId ? <TfiAngleRight /> : ''}
                {genreId && subGenreId && menuId ? menuName : ''}
              </div>
            </Navbar>
          </Col>
        </Form.Group>
        <Form.Group as={Row} className="mb-2">
          <Col sm={5} md={5} lg={5} xl={5}></Col>
          <Col sm={4} md={4} lg={4} xl={4}>
            <VscFoldDown size={50} />
          </Col>
          <Col sm={4} md={4} lg={4} xl={4}></Col>
        </Form.Group>
        <Col sm={4} md={4} lg={4} xl={4} className="mb-2" style={{ textDecoration: 'underline' }}>
          {t('DuplicationDisplayModal.to')}
        </Col>

        {(menuBookId || courseId) && genreId && (
          <Form.Group as={Row} className="mb-2">
            <Col sm={4} md={4} lg={4} xl={4}>
              {t('DuplicationDisplayModal.menu_book_course')}
            </Col>
            <Col sm={8} md={8} lg={8} xl={8}>
              {courseId && courses && courses.length > 0 && (
                <Form.Select onChange={handleChangeMenuBookOrCourse}>
                  <option value="">---Please Choose--- </option>
                  {courses?.map((course: Course) => {
                    return (
                      <option key={course.id} value={course.id}>
                        {course.name}
                      </option>
                    );
                  })}
                </Form.Select>
              )}
              {menuBookId && menuBooks && menuBooks.length > 0 && (
                <Form.Select onChange={handleChangeMenuBookOrCourse}>
                  <option value="">---Please Choose--- </option>
                  {menuBooks?.map((menuBook: MenuBook) => {
                    return (
                      <option key={menuBook.id} value={menuBook.id}>
                        {menuBook.name}
                      </option>
                    );
                  })}
                </Form.Select>
              )}
            </Col>
          </Form.Group>
        )}

        {(menuBookId || courseId) && genreId && subGenreId && (
          <Form.Group as={Row} className="mb-2">
            <Col sm={4} md={4} lg={4} xl={4}>
              {t('DuplicationDisplayModal.genre')}
            </Col>
            <Col sm={8} md={8} lg={8} xl={8}>
              <Form.Select className="genre" onChange={handleChangeGenre}>
                <option value="">---Please Choose--- </option>
                {genres &&
                  genres.map((genre) => {
                    return (
                      <option key={genre.id} value={genre.id}>
                        {genre.name}
                      </option>
                    );
                  })}
              </Form.Select>
            </Col>
          </Form.Group>
        )}

        {(menuBookId || courseId) && genreId && subGenreId && menuId && (
          <Form.Group as={Row} className="mb-2">
            <Col sm={4} md={4} lg={4} xl={4}>
              {t('DuplicationDisplayModal.subGenre')}
            </Col>
            <Col sm={8} md={8} lg={8} xl={8}>
              <Form.Select className="sub-genre" onChange={handleChangeSubGenre}>
                <option value="">---Please Choose--- </option>
                {subGenres &&
                  subGenres.map((subGenre) => {
                    return (
                      <option key={subGenre.id} value={subGenre.id}>
                        {subGenre.name}
                      </option>
                    );
                  })}
              </Form.Select>
            </Col>
          </Form.Group>
        )}
        <Form.Group as={Row} className="mt-5">
          <Col sm={5} md={5} lg={5} xl={5}></Col>
          <Col sm={4} md={4} lg={4} xl={4}>
            <Button variant="success" onClick={handleSubmitCopy}>
              {t('DuplicationDisplayModal.copy')}
            </Button>
          </Col>
          <Col sm={4} md={4} lg={4} xl={4}></Col>
        </Form.Group>
      </Modal.Body>
    </Modal>
  );
}
export default CopyModal;
